import React, { useState, useEffect } from 'react';
import { Shield, Key, Save, Eye, EyeOff, CreditCard, AlertTriangle, Building2 } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDateTime } from '../../utils/dateUtils';
import { APP_NAME } from '../../constants/app';

interface SystemSetting {
  value: string;
  description: string;
  isEncrypted: boolean;
  updatedAt?: string;
}

interface SystemSettingsData {
  KUDOSITY_API_KEY?: SystemSetting;
  KUDOSITY_API_SECRET?: SystemSetting;
  KUDOSITY_SENDER_ID?: SystemSetting;
  STRIPE_DEFAULT_MODE?: SystemSetting;
}

export const SystemSettings: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SystemSettingsData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    KUDOSITY_API_KEY: '',
    KUDOSITY_API_SECRET: '',
    KUDOSITY_SENDER_ID: '',
    STRIPE_DEFAULT_MODE: 'live'
  });
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [accountStripeMode, setAccountStripeMode] = useState<'test' | 'live' | null>(null);
  const [savingAccountMode, setSavingAccountMode] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchSettings();
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setAccounts(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const fetchAccountStripeMode = async (accountId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.account) {
        setAccountStripeMode(data.account.stripeMode || null);
      }
    } catch (error) {
      console.error('Failed to fetch account Stripe mode:', error);
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      fetchAccountStripeMode(selectedAccountId);
    } else {
      setAccountStripeMode(null);
    }
  }, [selectedAccountId]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/system-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setSettings(data.data || {});
        // Initialize form data with existing values
        setFormData({
          KUDOSITY_API_KEY: data.data?.KUDOSITY_API_KEY?.value || '',
          KUDOSITY_API_SECRET: data.data?.KUDOSITY_API_SECRET?.value || '',
          KUDOSITY_SENDER_ID: data.data?.KUDOSITY_SENDER_ID?.value || '',
          STRIPE_DEFAULT_MODE: data.data?.STRIPE_DEFAULT_MODE?.value || 'live'
        });
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key: string) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/system-settings/${key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: formData[key as keyof typeof formData],
          description: key === 'KUDOSITY_API_KEY' 
            ? 'Kudosity API Key for SMS 2FA'
            : key === 'KUDOSITY_API_SECRET'
            ? 'Kudosity API Secret for SMS 2FA'
            : key === 'KUDOSITY_SENDER_ID'
            ? 'Kudosity Sender ID for SMS 2FA'
            : 'Default Stripe mode for all accounts (test or live)',
          isEncrypted: key === 'KUDOSITY_API_SECRET'
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSettings();
        alert(t('admin.systemSettings.saved') || 'Setting saved successfully');
      } else {
        alert(data.message || t('admin.systemSettings.saveFailed') || 'Failed to save setting');
      }
    } catch (error: any) {
      console.error('Failed to save setting:', error);
      alert(error.message || t('admin.systemSettings.saveFailed') || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-6">
        <Shield className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.systemSettings.title') || 'System Settings'}
        </h2>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('admin.systemSettings.description') || 'Manage system-wide configuration settings. These settings apply to all accounts.'}
        </p>

        {/* Kudosity API Key */}
        <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.systemSettings.kudosityApiKey') || 'Kudosity API Key'}
          </label>
          <div className="flex space-x-2">
            <input
              type={showSecrets.KUDOSITY_API_KEY ? 'text' : 'password'}
              value={formData.KUDOSITY_API_KEY}
              onChange={(e) => setFormData({ ...formData, KUDOSITY_API_KEY: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={settings.KUDOSITY_API_KEY?.value ? '••••••••' : 'Enter API Key'}
            />
            <button
              type="button"
              onClick={() => toggleShowSecret('KUDOSITY_API_KEY')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {showSecrets.KUDOSITY_API_KEY ? (
                <EyeOff className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
            <button
              onClick={() => handleSave('KUDOSITY_API_KEY')}
              disabled={saving}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
          {settings.KUDOSITY_API_KEY?.updatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.systemSettings.lastUpdated') || 'Last updated'}: {formatDateTime(settings.KUDOSITY_API_KEY.updatedAt)}
            </p>
          )}
        </div>

        {/* Kudosity API Secret */}
        <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.systemSettings.kudosityApiSecret') || 'Kudosity API Secret'}
          </label>
          <div className="flex space-x-2">
            <input
              type={showSecrets.KUDOSITY_API_SECRET ? 'text' : 'password'}
              value={formData.KUDOSITY_API_SECRET}
              onChange={(e) => setFormData({ ...formData, KUDOSITY_API_SECRET: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={settings.KUDOSITY_API_SECRET?.value ? '••••••••' : 'Enter API Secret'}
            />
            <button
              type="button"
              onClick={() => toggleShowSecret('KUDOSITY_API_SECRET')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {showSecrets.KUDOSITY_API_SECRET ? (
                <EyeOff className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
            <button
              onClick={() => handleSave('KUDOSITY_API_SECRET')}
              disabled={saving}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
          {settings.KUDOSITY_API_SECRET?.updatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.systemSettings.lastUpdated') || 'Last updated'}: {formatDateTime(settings.KUDOSITY_API_SECRET.updatedAt)}
            </p>
          )}
        </div>

        {/* Kudosity Sender ID */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.systemSettings.kudositySenderId') || 'Kudosity Sender ID'}
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={formData.KUDOSITY_SENDER_ID}
              onChange={(e) => setFormData({ ...formData, KUDOSITY_SENDER_ID: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={APP_NAME}
            />
            <button
              onClick={() => handleSave('KUDOSITY_SENDER_ID')}
              disabled={saving}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
          {settings.KUDOSITY_SENDER_ID?.updatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.systemSettings.lastUpdated') || 'Last updated'}: {formatDateTime(settings.KUDOSITY_SENDER_ID.updatedAt)}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.systemSettings.senderIdHint') || 'Default: 3PM Escrow'}
          </p>
        </div>

        {/* Stripe Configuration */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Stripe Configuration
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Set the default Stripe mode for all accounts. Individual accounts can override this setting.
          </p>

          {/* Stripe Mode Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Default Stripe Mode
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, STRIPE_DEFAULT_MODE: 'test' });
                }}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  formData.STRIPE_DEFAULT_MODE === 'test'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Test Mode</span>
                </div>
                <p className="text-xs mt-1 opacity-75">Use test cards (4242...)</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, STRIPE_DEFAULT_MODE: 'live' });
                }}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  formData.STRIPE_DEFAULT_MODE === 'live'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Live Mode</span>
                </div>
                <p className="text-xs mt-1 opacity-75">Real payments</p>
              </button>
            </div>
          </div>

          {/* Warning Banner for Test Mode */}
          {formData.STRIPE_DEFAULT_MODE === 'test' && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                    Test Mode Active
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                    All accounts will use Stripe test mode by default. Test cards like 4242 4242 4242 4242 will work.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <button
              onClick={() => handleSave('STRIPE_DEFAULT_MODE')}
              disabled={saving}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
          {settings.STRIPE_DEFAULT_MODE?.updatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('admin.systemSettings.lastUpdated') || 'Last updated'}: {formatDateTime(settings.STRIPE_DEFAULT_MODE.updatedAt)}
            </p>
          )}
        </div>

        {/* Per-Account Stripe Mode Override */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Per-Account Stripe Mode Override
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Override Stripe mode for specific accounts. Leave as "Use Global Default" to inherit the system default.
          </p>

          {/* Account Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Account
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">-- Select an account --</option>
              {accounts.map((account) => (
                <option key={account._id} value={account._id}>
                  {account.companyName}
                </option>
              ))}
            </select>
          </div>

          {/* Account Stripe Mode Selector */}
          {selectedAccountId && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Stripe Mode for {accounts.find(a => a._id === selectedAccountId)?.companyName}
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setAccountStripeMode(null)}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors text-left ${
                    accountStripeMode === null
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">Use Global Default</span>
                  </div>
                  <p className="text-xs mt-1 opacity-75">
                    {formData.STRIPE_DEFAULT_MODE === 'test' ? 'Test Mode' : 'Live Mode'} (from system settings)
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setAccountStripeMode('test')}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors text-left ${
                    accountStripeMode === 'test'
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Force Test Mode</span>
                  </div>
                  <p className="text-xs mt-1 opacity-75">Always use test mode for this account</p>
                </button>
                <button
                  type="button"
                  onClick={() => setAccountStripeMode('live')}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors text-left ${
                    accountStripeMode === 'live'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span className="font-medium">Force Live Mode</span>
                  </div>
                  <p className="text-xs mt-1 opacity-75">Always use live mode for this account</p>
                </button>
              </div>
            </div>
          )}

          {/* Save Button */}
          {selectedAccountId && (
            <div className="flex space-x-2">
              <button
                onClick={async () => {
                  setSavingAccountMode(true);
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${apiUrl}/api/accounts/${selectedAccountId}/stripe-mode`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        mode: accountStripeMode
                      })
                    });

                    const data = await response.json();
                    if (data.success) {
                      alert('Account Stripe mode updated successfully');
                      await fetchAccountStripeMode(selectedAccountId);
                    } else {
                      alert(data.message || 'Failed to update account Stripe mode');
                    }
                  } catch (error: any) {
                    console.error('Failed to save account Stripe mode:', error);
                    alert(error.message || 'Failed to update account Stripe mode');
                  } finally {
                    setSavingAccountMode(false);
                  }
                }}
                disabled={savingAccountMode}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{savingAccountMode ? 'Saving...' : 'Save Account Mode'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

